// Uploaded files for sources. Files are parsed and kept in the data worker;
// the editor keeps a small summary of each:
//   { key, fileName, sheets: [{ name, rowCount, columnCount }], sheetName, columnOverrides? }
import { useEffect, useState } from "react";
import { dataHost } from "../data/dataHost";
import { logTimings, now } from "./perfLog";

export const MAX_FILE_MB = 50;

export const TYPE_OPTIONS_LIST = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "boolean", label: "True/False" },
];

export const readWorkbook = async (file) => {
  if (file.size > MAX_FILE_MB * 1024 * 1024) {
    throw new Error(`files larger than ${MAX_FILE_MB} MB aren't supported in the browser yet`);
  }
  const start = now();
  const workbook = await dataHost.loadFile(file);
  logTimings("upload", start, {
    format: /\.(csv|txt|tsv)$/i.test(file.name || "") ? "csv" : "xlsx",
    sizeMB: Math.round((file.size / 1048576) * 10) / 10,
    sheets: workbook.sheets.map((sheet) => ({ rows: sheet.rowCount, columns: sheet.columnCount })),
  });
  if (!workbook.sheets.length) throw new Error("the file has no sheets");
  return workbook;
};

// The first sheet with data (or the first sheet).
export const firstSheet = (workbook) => workbook.sheets.find((s) => s.rowCount) || workbook.sheets[0];

// A new source's definition read from a sheet (the editor gives it its id).
export const describeNewSource = (workbook, sheetName) =>
  dataHost.describeSource(
    { key: workbook.key, sheetName },
    { id: "source", label: workbook.fileName.replace(/\.[^.]+$/, ""), originalName: workbook.fileName, sheetName },
  );

// Re-reads a source's columns after its header settings, sheet or file
// change, then updates the editor. Only the latest change per source applies.
const reshaping = new Map();
export const reshapeSource = async (dispatch, source, currentFile, settings, newFile) => {
  const request = (reshaping.get(source.id) || 0) + 1;
  reshaping.set(source.id, request);
  const file = newFile ?? currentFile;
  const merged = { ...source, ...settings };
  const sheetName = settings.sheetName ?? file?.sheetName;
  const rebuilt = file
    ? await dataHost.describeSource(
        { key: file.key, sheetName },
        {
          id: source.id,
          label: source.label,
          matchBy: merged.matchBy,
          originalName: file.fileName ?? source.originalName,
          sheetName,
          hasHeader: merged.hasHeader,
          headerRow: merged.headerRow,
        },
      )
    : null;
  if (reshaping.get(source.id) === request) dispatch({ type: "reshapeSource", id: source.id, settings, file: newFile, source: rebuilt });
};

// How each chosen file matches its saved source, checked in the worker:
// { [sourceId]: { ok, issues, rowCount, letters } | null }.
export const useSourceChecks = (sources, files, overrides) => {
  const [checks, setChecks] = useState({});
  useEffect(() => {
    let live = true;
    Promise.all(
      sources.map(async (source) => {
        const file = files[source.id];
        if (!file) return [source.id, null];
        const fileRef = { key: file.key, sheetName: file.sheetName, columnOverrides: overrides?.[source.id] ?? file.columnOverrides };
        try {
          return [source.id, await dataHost.checkSource(source, fileRef)];
        } catch (error) {
          return [source.id, { ok: false, issues: [{ level: "error", column: "", message: error.message }], rowCount: 0, letters: [] }];
        }
      }),
    ).then((entries) => live && setChecks(Object.fromEntries(entries)));
    return () => {
      live = false;
    };
  }, [sources, files, overrides]);
  return checks;
};
